import {
  LatLon,
  WorldPoint,
  RoadSegment,
  BuildingBox,
  NeighborhoodTree,
  WasteBasket,
  SurveillanceCamera,
  InspectorNPC,
  TeaStallEntity,
  ChiliTrap,
  DummyWalkerNPC,
} from '../domain/types.ts';
import { OsmClient } from './osmClient.ts';
import { Projection } from './projection.ts';
import { createNeighborhoodMap, createInitialDummyWalkers } from './mapData.ts';
import { GeneratedConditions } from '../services/aiMapGenerator.ts';
import { createChibiPedestrians } from '../entities/npc.ts';

export interface RealMapData {
  roads: RoadSegment[];
  buildings: BuildingBox[];
  trees: NeighborhoodTree[];
  wasteBaskets: WasteBasket[];
  cameras: SurveillanceCamera[];
  inspectors: InspectorNPC[];
  teaStalls: TeaStallEntity[];
  chiliTraps: ChiliTrap[];
  dummyWalkers: DummyWalkerNPC[];
  radiusM: number;
  center: LatLon;
  label: string;
  isRealOsm: boolean;
}

export function getMapCacheKey(center: { lat: number; lon: number }): string {
  return `dogpoop_osm_cache_${center.lat.toFixed(4)}_${center.lon.toFixed(4)}`;
}

export function getCachedMap(center: { lat: number; lon: number }): RealMapData | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const key = getMapCacheKey(center);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RealMapData;
    if (parsed && Array.isArray(parsed.roads) && parsed.roads.length > 0) {
      return parsed;
    }
  } catch (err) {
    console.warn('[RealMapLoader] Lỗi đọc cache map:', err);
  }
  return null;
}

export function setCachedMap(center: { lat: number; lon: number }, mapData: RealMapData): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const key = getMapCacheKey(center);
    const serialized = JSON.stringify(mapData);
    try {
      localStorage.setItem(key, serialized);
    } catch (e: any) {
      const isQuota =
        e?.name === 'QuotaExceededError' ||
        e?.code === 22 ||
        e?.code === 1014 ||
        e?.number === -2147024882;
      if (isQuota) {
        console.warn('[RealMapLoader] QuotaExceededError, dọn bớt cache map cũ...');
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith('dogpoop_osm_cache_') && k !== key) {
            keysToRemove.push(k);
          }
        }
        for (const k of keysToRemove) {
          localStorage.removeItem(k);
        }
        try {
          localStorage.setItem(key, serialized);
        } catch (retryErr) {
          console.error('[RealMapLoader] Vẫn không thể lưu cache map sau khi xóa bớt:', retryErr);
        }
      } else {
        console.warn('[RealMapLoader] Lỗi lưu cache map:', e);
      }
    }
  } catch (err) {
    console.warn('[RealMapLoader] Lỗi setCachedMap:', err);
  }
}

export class RealMapLoader {
  public static getMapCacheKey = getMapCacheKey;
  public static getCachedMap = getCachedMap;
  public static setCachedMap = setCachedMap;

  /**
   * Tải bản đồ thực tế từ OpenStreetMap dựa vào tọa độ GPS (bán kính co giãn lên đến 200m)
   * Đảm bảo mọi nhân vật xuất phát và di chuyển 100% bám trên lòng đường thực tế!
   */
  public static async loadRealMap(
    center: LatLon,
    label: string,
    aiConditions?: GeneratedConditions
  ): Promise<RealMapData> {
    const cached = RealMapLoader.getCachedMap(center);
    if (cached) {
      if (aiConditions) {
        const mapGenerated = RealMapLoader.populateEntitiesAlongRealRoads(
          cached.roads,
          label || cached.label,
          aiConditions
        );
        return {
          ...cached,
          label: label || cached.label,
          buildings: mapGenerated.buildings,
          trees: mapGenerated.trees,
          wasteBaskets:
            cached.wasteBaskets && cached.wasteBaskets.length > 0
              ? cached.wasteBaskets
              : mapGenerated.wasteBaskets,
          cameras: mapGenerated.cameras,
          inspectors: mapGenerated.inspectors,
          teaStalls: mapGenerated.teaStalls,
          chiliTraps: mapGenerated.chiliTraps,
          dummyWalkers: mapGenerated.dummyWalkers,
        };
      }
      return {
        ...cached,
        label: label || cached.label,
      };
    }

    const rawData = await OsmClient.fetchNeighborhoodData(center, 200);

    if (rawData && rawData.elements.length > 0) {
      const roads: RoadSegment[] = [];
      const wasteBaskets: WasteBasket[] = [];
      let maxDistM = 100;

      let roadIdx = 0;
      for (const el of rawData.elements) {
        if (el.type === 'way' && el.geometry && el.geometry.length >= 2) {
          const points: WorldPoint[] = el.geometry.map((g) => {
            const pt = Projection.project(g, center);
            const dist = Math.hypot(pt.x, pt.y);
            if (dist > maxDistM) maxDistM = dist;
            return pt;
          });

          const name = el.tags?.name || el.tags?.ref || `Đường ${++roadIdx}`;
          const highwayType = el.tags?.highway || 'residential';
          const width =
            highwayType === 'primary' || highwayType === 'secondary'
              ? 11
              : highwayType === 'footway' || highwayType === 'pedestrian'
              ? 5
              : 8;

          roads.push({
            id: `osm-road-${el.id}`,
            name,
            width,
            points,
            isSidewalk: highwayType === 'footway' || highwayType === 'pedestrian',
          });
        } else if (el.type === 'node' && el.lat !== undefined && el.lon !== undefined) {
          const pt = Projection.project({ lat: el.lat, lon: el.lon }, center);
          if (el.tags?.amenity === 'waste_basket') {
            wasteBaskets.push({ id: `osm-wb-${el.id}`, x: pt.x, y: pt.y });
          }
        }
      }

      if (roads.length >= 1) {
        // Chuẩn hóa bán kính khu phố 100m để camera bao quát vừa vặn, sắc nét như ảnh mẫu cũ
        const radiusM = 100;
        const mapGenerated = RealMapLoader.populateEntitiesAlongRealRoads(roads, label, aiConditions);

        const realMapResult: RealMapData = {
          roads,
          buildings: mapGenerated.buildings,
          trees: mapGenerated.trees,
          wasteBaskets: wasteBaskets.length > 0 ? wasteBaskets : mapGenerated.wasteBaskets,
          cameras: mapGenerated.cameras,
          inspectors: mapGenerated.inspectors,
          teaStalls: mapGenerated.teaStalls,
          chiliTraps: mapGenerated.chiliTraps,
          dummyWalkers: mapGenerated.dummyWalkers,
          radiusM,
          center,
          label,
          isRealOsm: true,
        };

        RealMapLoader.setCachedMap(center, realMapResult);
        return realMapResult;
      }
    }

    // Fallback: Ngõ 165 Cầu Giấy
    const fallbackBase = createNeighborhoodMap('cau-giay');
    return {
      ...fallbackBase,
      dummyWalkers: createInitialDummyWalkers('cau-giay'),
      radiusM: 100,
      center,
      label,
      isRealOsm: false,
    };
  }

  /**
   * Phân bổ thông minh các POI Lý thuyết Trò chơi (Camera, Quán Trà Đá, Bác Tổ Trưởng, Dummy Walkers)
   * 100% bám trên mặt đường thực tế OSM xung quanh vị trí tâm người chơi (0, 0)!
   */
  private static populateEntitiesAlongRealRoads(
    roads: RoadSegment[],
    label: string,
    aiConditions?: GeneratedConditions
  ) {
    const buildings: BuildingBox[] = [];
    const trees: NeighborhoodTree[] = [];
    const wasteBaskets: WasteBasket[] = [];
    const cameras: SurveillanceCamera[] = [];
    const inspectors: InspectorNPC[] = [];
    const teaStalls: TeaStallEntity[] = [];
    const chiliTraps: ChiliTrap[] = [];

    // Tìm các tuyến đường gần tâm (0, 0) nhất
    const roadsNearCenter = [...roads]
      .map((r) => {
        let minDist = Infinity;
        let closestPt = r.points[0] || { x: 0, y: 0 };
        for (const pt of r.points) {
          const d = Math.hypot(pt.x, pt.y);
          if (d < minDist) {
            minDist = d;
            closestPt = pt;
          }
        }
        return { road: r, minDist, closestPt };
      })
      .sort((a, b) => a.minDist - b.minDist);

    const mainEntry = roadsNearCenter[0] || { road: roads[0]!, minDist: 0, closestPt: { x: 0, y: 0 } };
    const mainRoad = mainEntry.road;

    // Helper snap một điểm bất kỳ vào lề đường của tuyến đường gần nhất
    const snapToCurb = (
      prefX: number,
      prefY: number,
      bldgW: number,
      bldgH: number
    ): { x: number; y: number; normalAngle: number; curbEdgePt: WorldPoint } => {
      // Tìm đoạn thẳng trong các road gần tâm nhất
      let bestDist = Infinity;
      let resX = prefX;
      let resY = prefY;
      let normalAngle = 0;
      let curbEdgePt = { x: prefX, y: prefY };

      for (let rIdx = 0; rIdx < Math.min(roadsNearCenter.length, 5); rIdx++) {
        const curR = roadsNearCenter[rIdx]!.road;
        for (let i = 0; i < curR.points.length - 1; i++) {
          const pA = curR.points[i]!;
          const pB = curR.points[i + 1]!;
          const dx = pB.x - pA.x;
          const dy = pB.y - pA.y;
          const lenSq = dx * dx + dy * dy;
          if (lenSq < 0.1) continue;

          let t = ((prefX - pA.x) * dx + (prefY - pA.y) * dy) / lenSq;
          t = Math.max(0, Math.min(1, t));
          const projX = pA.x + t * dx;
          const projY = pA.y + t * dy;

          const toPrefX = prefX - projX;
          const toPrefY = prefY - projY;
          const toPrefDist = Math.hypot(toPrefX, toPrefY);

          if (toPrefDist < bestDist) {
            bestDist = toPrefDist;
            const len = Math.sqrt(lenSq);
            // Hướng từ tim đường ra điểm mong muốn
            let nx = toPrefDist > 0.001 ? toPrefX / toPrefDist : -dy / len;
            let ny = toPrefDist > 0.001 ? toPrefY / toPrefDist : dx / len;
            normalAngle = Math.atan2(ny, nx);

            // Khoảng cách từ tim đường ra tâm nhà: (nửa lòng đường + nửa bề rộng nhà + lề vỉa hè)
            const curbOffset = curR.width / 2 + Math.max(bldgW, bldgH) / 2 + 1.5;
            resX = projX + nx * curbOffset;
            resY = projY + ny * curbOffset;
            curbEdgePt = {
              x: projX + nx * (curR.width / 2 + 1.2),
              y: projY + ny * (curR.width / 2 + 1.2),
            };
          }
        }
      }

      return {
        x: Math.round(resX - bldgW / 2),
        y: Math.round(resY - bldgH / 2),
        normalAngle,
        curbEdgePt,
      };
    };

    // Helper thêm tòa nhà có kiểm tra chống đè (AABB collision check)
    const placeBuilding = (b: BuildingBox) => {
      let attempts = 0;
      while (attempts < 8) {
        let collision = false;
        for (const existing of buildings) {
          const overlapX =
            Math.abs(b.x + b.width / 2 - (existing.x + existing.width / 2)) <
            (b.width + existing.width) / 2 + 3;
          const overlapY =
            Math.abs(b.y + b.height / 2 - (existing.y + existing.height / 2)) <
            (b.height + existing.height) / 2 + 3;
          if (overlapX && overlapY) {
            collision = true;
            // Dịch chuyển nhẹ dọc theo hướng lùi ra xa
            if (b.x >= 0) b.x += 16;
            else b.x -= 16;
            break;
          }
        }
        if (!collision) break;
        attempts++;
      }
      buildings.push(b);
    };

    // 1. Quán Trà Đá Vỉa Hè (Radar 360 độ): Đặt ở mép vỉa hè phía Tây gần tâm
    const teaStallPos = snapToCurb(-16, -4, 6, 6);
    const teaStallName =
      aiConditions?.landmarks.find((l) => l.type === 'tea_stall')?.name ||
      `Quán Nước Cóc Cô Hoa`;
    teaStalls.push({
      id: 'real-teastall-1',
      name: teaStallName,
      x: teaStallPos.curbEdgePt.x,
      y: teaStallPos.curbEdgePt.y,
      radius: 20,
      activeComment: undefined,
      commentTimer: 0,
      cooldownTimer: 3000,
    });

    // 2. Tiệm Vàng Kim Tài (Camera 4K): Đặt ở phía Tây-Bắc lề đường
    const kimTaiPos = snapToCurb(-34, -20, 14, 18);
    placeBuilding({
      id: 'bldg-kimtai',
      name: 'Tiệm Vàng Kim Tài (Camera 4K)',
      x: kimTaiPos.x,
      y: kimTaiPos.y,
      width: 14,
      height: 18,
      hasCamera: true,
      ownerName: 'Ông Kim',
    });
    // Camera rọi xuống tim đường
    cameras.push({
      id: 'cam-kimtai',
      buildingName: 'Tiệm Vàng Kim Tài',
      x: kimTaiPos.curbEdgePt.x,
      y: kimTaiPos.curbEdgePt.y,
      facingAngle: kimTaiPos.normalAngle + Math.PI,
      fov: (70 * Math.PI) / 180,
      range: 16,
      isBlinking: true,
    });

    // 3. Nhà Bác Tổ Trưởng Dân Phố: Đặt ở phía Bắc lề đường
    const toTruongPos = snapToCurb(-8, -24, 12, 16);
    placeBuilding({
      id: 'bldg-totruong',
      name: 'Nhà Bác Tổ Trưởng',
      x: toTruongPos.x,
      y: toTruongPos.y,
      width: 12,
      height: 16,
      hasCamera: false,
      ownerName: 'Bác Bình Tổ Trưởng',
    });

    // 4. Tạp Hóa Chị Loan: Đặt ở phía Tây-Nam lề đường
    const tapHoaPos = snapToCurb(-36, 18, 16, 14);
    placeBuilding({
      id: 'bldg-taphoa',
      name: 'Tạp Hóa Chị Loan',
      x: tapHoaPos.x,
      y: tapHoaPos.y,
      width: 16,
      height: 14,
      hasCamera: false,
      ownerName: 'Chị Loan',
    });

    // 5. Chung Cư Mini 7 Tầng (Camera 4K): Đặt ở phía Nam dưới tâm
    const ccMiniPos = snapToCurb(-4, 24, 18, 20);
    placeBuilding({
      id: 'bldg-ccmini',
      name: 'Chung Cư Mini 7 Tầng',
      x: ccMiniPos.x,
      y: ccMiniPos.y,
      width: 18,
      height: 20,
      hasCamera: true,
      ownerName: 'Ban Quản Trị CCMini',
    });
    cameras.push({
      id: 'cam-ccmini',
      buildingName: 'Chung Cư Mini 7 Tầng',
      x: ccMiniPos.curbEdgePt.x,
      y: ccMiniPos.curbEdgePt.y,
      facingAngle: ccMiniPos.normalAngle + Math.PI,
      fov: (70 * Math.PI) / 180,
      range: 16,
      isBlinking: true,
    });

    // 6. Nhà Số 12 (Bẫy ớt bột): Đặt ở phía Đông-Bắc
    const so12Pos = snapToCurb(18, -20, 12, 16);
    placeBuilding({
      id: 'bldg-so12',
      name: 'Nhà Số 12 (Bẫy ớt bột)',
      x: so12Pos.x,
      y: so12Pos.y,
      width: 12,
      height: 16,
      ownerName: 'Bác Tư Khó Tính',
    });
    // Bẫy ớt đặt trên vỉa hè trước cửa nhà 12
    chiliTraps.push({
      id: 'real-chili-1',
      x: so12Pos.curbEdgePt.x,
      y: so12Pos.curbEdgePt.y,
      radius: 3.5,
    });

    // 7. Nhà Nuôi 3 Chó Hoang: Đặt ở phía Đông ngoài
    const choHoangPos = snapToCurb(38, -18, 14, 16);
    placeBuilding({
      id: 'bldg-chohoang',
      name: 'Nhà Nuôi 3 Chó Hoang (Mục tiêu đổ tội)',
      x: choHoangPos.x,
      y: choHoangPos.y,
      width: 14,
      height: 16,
      ownerName: 'Nhà Cô Mùi (Nuôi chó)',
    });

    // 8. Cơm Bình Dân Đầu Hẻm: Đặt ở phía Đông-Nam
    const comBinhDanPos = snapToCurb(28, 20, 16, 16);
    placeBuilding({
      id: 'bldg-combinhdan',
      name: 'Cơm Bình Dân Đầu Hẻm',
      x: comBinhDanPos.x,
      y: comBinhDanPos.y,
      width: 16,
      height: 16,
      ownerName: 'Anh Hùng',
    });

    // 9. Hai khối nhà mờ ở 2 góc hậu cảnh (chuẩn mực mẫu cũ tạo chiều sâu)
    placeBuilding({
      id: 'ambient-bldg-left',
      name: 'Nhà dân mờ',
      x: -56,
      y: -28,
      width: 15,
      height: 16,
      isAmbient: true,
    });
    placeBuilding({
      id: 'ambient-bldg-right',
      name: 'Nhà dân mờ',
      x: 42,
      y: 16,
      width: 16,
      height: 14,
      isAmbient: true,
    });

    // 10. Bác Tổ Trưởng Tuần Tra: Tuần tra dọc tuyến đường chính gần tâm
    const patrolPoints: WorldPoint[] = mainRoad.points.filter(
      (pt) => Math.hypot(pt.x, pt.y) <= 50
    );
    if (patrolPoints.length < 2) {
      patrolPoints.push({ x: -25, y: 0 }, { x: 25, y: 0 });
    }
    inspectors.push({
      id: 'real-inspector-1',
      name: aiConditions?.landmarks.find((l) => l.type === 'guard')?.name || 'Bác Tổ Trưởng Tuần Tra',
      x: patrolPoints[0]!.x,
      y: patrolPoints[0]!.y,
      patrolPoints,
      currentPointIndex: 0,
      speed: 1.4,
      facingAngle: 0,
      fov: (90 * Math.PI) / 180,
      range: 15,
      sweepAngle: 0,
    });

    // 11. Thùng rác công cộng (Đặt ở 2 đầu khu phố)
    const wbPos1 = snapToCurb(-42, -4, 4, 4);
    const wbPos2 = snapToCurb(42, -4, 4, 4);
    wasteBaskets.push(
      { id: 'real-wb-1', x: wbPos1.curbEdgePt.x, y: wbPos1.curbEdgePt.y },
      { id: 'real-wb-2', x: wbPos2.curbEdgePt.x, y: wbPos2.curbEdgePt.y }
    );

    // 12. Cây xanh vỉa hè: Rải đều 7 cây xanh ven đường giữa các nhà
    const treeOffsets = [
      { x: -22, y: -4 },
      { x: -5, y: -4 },
      { x: 10, y: -4 },
      { x: 24, y: -4 },
      { x: -18, y: 10 },
      { x: 18, y: 10 },
      { x: 30, y: -6 },
    ];
    treeOffsets.forEach((to, idx) => {
      const tp = snapToCurb(to.x, to.y, 4, 4);
      trees.push({ id: `real-tree-${idx}`, x: tp.curbEdgePt.x, y: tp.curbEdgePt.y });
    });

    // 13. Dummy Walker Rivals: Lộ trình tuần tra 100% bám theo các mốc đường thực tế!
    const road1 = mainRoad;
    const road2 = roadsNearCenter[1]?.road || road1;
    const p0 = road1.points[0] || { x: 0, y: 0 };
    const pMid1 = road1.points[Math.floor(road1.points.length / 2)] || { x: 10, y: 10 };
    const pEnd1 = road1.points[road1.points.length - 1] || { x: 20, y: 20 };

    const dummyWalkers: DummyWalkerNPC[] = [
      // Bảo Ngọc (Thánh Mẫu): Tuần tra dọc tuyến đường 1
      {
        id: 'walker-altruist',
        name: 'Bảo Ngọc (Thánh Mẫu)',
        archetype: 'altruist',
        x: pMid1.x,
        y: pMid1.y,
        facingAngle: 0,
        speed: 3.8,
        patrolRoute: [...road1.points].reverse(),
        currentRouteIndex: 0,
        dog: {
          x: pMid1.x - 1.5,
          y: pMid1.y,
          facingAngle: 0,
          bowels: 25,
          isPooping: false,
          poopTimer: 0,
          breadcrumbs: [],
        },
        isCleaning: false,
        cleanTimer: 0,
        isFleeing: false,
        fleeTimer: 0,
        hasWitnessedPlayerDefect: false,
        isSeekingRevenge: false,
        inChickenGameWithPlayer: false,
      },
      // Tuấn Lươn (Lén Lút): Tuần tra dọc tuyến đường 2
      {
        id: 'walker-defector',
        name: 'Tuấn Lươn (Lén Lút)',
        archetype: 'defector',
        x: road2.points[0]?.x ?? p0.x,
        y: road2.points[0]?.y ?? p0.y,
        facingAngle: Math.PI / 2,
        speed: 5.2,
        patrolRoute: [...road2.points],
        currentRouteIndex: 0,
        dog: {
          x: (road2.points[0]?.x ?? p0.x) - 1.5,
          y: road2.points[0]?.y ?? p0.y,
          facingAngle: Math.PI / 2,
          bowels: 65,
          isPooping: false,
          poopTimer: 0,
          breadcrumbs: [],
        },
        isCleaning: false,
        cleanTimer: 0,
        isFleeing: false,
        fleeTimer: 0,
        hasWitnessedPlayerDefect: false,
        isSeekingRevenge: false,
        inChickenGameWithPlayer: false,
      },
      // Hùng Còi (Tit-for-Tat): Tuần tra giữa hai tuyến đường
      {
        id: 'walker-titfortat',
        name: 'Hùng Còi (Tit-for-Tat)',
        archetype: 'tit_for_tat',
        x: pEnd1.x,
        y: pEnd1.y,
        facingAngle: -Math.PI / 2,
        speed: 4.2,
        patrolRoute: [pEnd1, pMid1, p0],
        currentRouteIndex: 0,
        dog: {
          x: pEnd1.x - 1.5,
          y: pEnd1.y,
          facingAngle: -Math.PI / 2,
          bowels: 40,
          isPooping: false,
          poopTimer: 0,
          breadcrumbs: [],
        },
        isCleaning: false,
        cleanTimer: 0,
        isFleeing: false,
        fleeTimer: 0,
        hasWitnessedPlayerDefect: false,
        isSeekingRevenge: false,
        inChickenGameWithPlayer: false,
      },
    ];

    // Thêm các NPC Chibi vỉa hè tuần tra trên mạng lưới đường thực tế
    const chibiNpcs = createChibiPedestrians(roads);
    dummyWalkers.push(...chibiNpcs);

    return {
      buildings,
      trees,
      wasteBaskets,
      cameras,
      inspectors,
      teaStalls,
      chiliTraps,
      dummyWalkers,
    };
  }
}
